/**
 * CoworkHero — redesigned hero for the Claude Cowork workshop landing page.
 *
 * LEFT-aligned copy column over a clay instructor photo that bleeds in from the
 * RIGHT under a seamless left→right cream gradient (plus a subtle bottom cream
 * fade). Below the copy/buttons sits a contained date list (top border, hairline
 * row dividers): each upcoming date shows a big display-font date + a segmented
 * seat meter + "N seats left" / "Last call", while sold-out/past dates collapse
 * into a compact "… — sold out" row with a small terra X mark.
 *
 * Cowork-only. Rendered from event-detail's isCowork branch. WorkshopHero is
 * untouched for every other workshop.
 *
 * Data: consumes the already-computed HairlineStop[] (same pipeline the legacy
 * hairline timeline used). Meter fill count + color + copy are derived from the
 * existing ClockResult fields — no new seat math.
 *
 * Design source: /tmp/hero-design/workshop-hero-final.dc.html. CSS lives in
 * client/src/index.css (`.ch-*` classes) — component-level CSS imports break the
 * build (see note in HairlineTimeline.tsx).
 */
import { useEffect, useRef, type ReactNode } from "react";
import type { HairlineStop } from "@/components/scarcity/HairlineTimeline";
import { displayCeilingFor, type ClockResult } from "@/components/scarcity/useSeatClock";
import { requestCheckout } from "@/lib/open-checkout";

const IMG_BASE = "/images/claude-cowork-hero-clay-shoulder";

const dayOfWeekFmt = (dt: Date, tz: string) =>
  dt.toLocaleDateString("en-US", { weekday: "long", timeZone: tz });
const monthDayFmt = (dt: Date, tz: string) =>
  dt.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: tz });
/** "Tuesday, September 15" — the whole date on ONE line. Sales asked for the
 *  weekday beside the date rather than stacked above it (Isaac, 2026-09-09). */
const inlineDateFmt = (dt: Date, tz: string) =>
  dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: tz });

/** "10:00 AM to 1:30 PM ET" — the session's own start and end, in its own zone.
 *
 *  Formatted from the dates rather than written into the card's copy, so a
 *  session that moves cannot leave a stale time on screen. "to" rather than a
 *  dash because the house voice spells ranges out (see the events table's own
 *  `duration_description`: "3.5 hours (10:00 AM to 1:30 PM ET)").
 *
 *  The zone abbreviation comes from the formatter, so a session after DST ends
 *  reads EST without anyone remembering to change it. */
const timeRangeFmt = (start: Date, end: Date, tz: string) => {
  const t = (d: Date) =>
    d.toLocaleTimeString("en-US", {
      hour: "numeric", minute: "2-digit", timeZone: tz,
    });
  const zone = end
    .toLocaleTimeString("en-US", { timeZoneName: "short", timeZone: tz })
    .split(" ")
    .pop();
  return `${t(start)} to ${t(end)} ${zone}`;
};

/** The Mon Sep 7 2026 session runs on Labor Day and gets a hand-lettered
 *  callout badge on its date card. Matched by ET calendar date (en-CA gives
 *  YYYY-MM-DD) so a UTC-vs-ET boundary can't misattribute it. */
const isLaborDaySession = (dt: Date, tz: string) =>
  dt.toLocaleDateString("en-CA", { timeZone: tz }) === "2026-09-07";

/**
 * The "filling" / medium meter color — gold/amber, the third state between
 * forest (plenty) and terra (last-call / critical). Inline literal on purpose:
 * client/src/index.css is owned by another agent right now, so this color must
 * NOT be promoted to a CSS variable / token there. Keep it here.
 */
const METER_GOLD = "hsl(38, 82%, 50%)";

/** Derive the segmented-meter fill count, color, and copy for an open stop from
 *  the existing ClockResult. fillLevel (1-3) maps onto `totalSegments`. Three
 *  color states: terra at last-call / tight (level 1), GOLD/AMBER for the
 *  "filling" medium band (the `medium` flag, set by the monotonic-depletion pass
 *  on a nearer-but-clamped date), forest otherwise (plenty / later dates). Copy
 *  mirrors HairlineTimeline.meterFor: "N seats open" at the widest state,
 *  "N seats left" as it depletes, "Last call" for the last-call window. */
function deriveMeter(
  clock: ClockResult | undefined,
  startingCount: number,
  totalSegments: number,
): { filled: number; color: string; text: string; isLastCall: boolean } {
  // Pre-mount / SSR-neutral: wide open at the display ceiling.
  if (!clock) {
    return {
      filled: Math.max(1, Math.round((3 / 4) * totalSegments)),
      color: "var(--forest)",
      text: `${displayCeilingFor(startingCount)} seats open`,
      isLastCall: false,
    };
  }
  const isLastCall = clock.state === "last-call";
  const seats = clock.seatsLeft ?? displayCeilingFor(startingCount);
  // ClockResult.fillLevel is on a 4-step scale (1-3 ever render). Scale it onto
  // the design's segment count so a wide-open meter ≈ 3/4 of the bars.
  const filled = Math.max(1, Math.min(totalSegments, Math.round((clock.fillLevel / 4) * totalSegments)));
  const tight = clock.fillLevel <= 1 || isLastCall;
  // Three-state color: terra (critical) → gold (filling/medium) → forest (plenty).
  const color = tight ? "var(--terra)" : clock.medium ? METER_GOLD : "var(--forest)";
  const text = clock.fillLevel >= 3 ? `${seats} seats open` : `${seats} seats left`;
  return { filled, color, text, isLastCall };
}

/** Segmented seat meter. `total` segments, `filled` colored, rest charcoal-14. */
function SeatMeter({ filled, total, color }: { filled: number; total: number; color: string }) {
  return (
    <span className="ch-meter" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => (
        <i
          key={i}
          className={i < filled ? "is-filled" : undefined}
          style={{ background: i < filled ? color : "var(--charcoal-14)" }}
        />
      ))}
    </span>
  );
}

export interface CoworkHeroProps {
  eyebrow: string;
  headline: ReactNode;
  /** The headline as ONE clean sentence, for the accessibility tree.
   *
   *  Set it only when `headline` carries decorative markup that duplicates or
   *  fragments words — the Sales page's proofreader's-caret insert stacks
   *  "practical" above a caret between "The" and "Claude", so the visible
   *  markup says the sentence in a different order than a reader hears it.
   *  When set, the <h1> holds this string alone (sr-only) and the styled
   *  headline renders beside it in an aria-hidden div carrying the same
   *  `.ch-h1` class, so every stylesheet rule still lands. Same construction as
   *  the kids page (`.kids-h1` + `<h1 className="sr-only">`).
   *
   *  Unset (every other page): today's `<h1 className="ch-h1">` is untouched. */
  headlinePlain?: string;
  subtitle?: ReactNode;
  /** Price shown directly under the subtitle. Only set by the ?price=1
   *  hero-price test; undefined keeps the page as it was. */
  priceLine?: string;
  /** Pre-discount price, struck through before priceLine (ad coupon). */
  priceStrike?: string;
  /** Same scarcity pipeline the legacy hairline timeline consumed. */
  hairlineStops: HairlineStop[];
  onSelectSession?: (id: number) => void;
  primaryCta: { label: string; href: string };
  secondaryCta: { label: string; href: string };
  /** Responsive image base path for the bleeding right-side portrait. Expects
   *  `{base}.webp` + `{base}-480/-800/-1200.webp` variants. Defaults to the
   *  Cowork clay-shoulder portrait of Isaac (shared instructor). */
  imageBase?: string;
  /** Optional MOBILE plate, art-directed rather than merely smaller. Expects
   *  `{base}.webp` + `-480/-800/-1200`. Use it when the desktop plate's
   *  composition does not survive a narrow box — the Sales pair is the case: its
   *  desktop plate is right-anchored AND has the left fade painted into the
   *  pixels (see scripts/_sales-hero-plate.py), which on mobile reads as a white
   *  wedge over the picture. A width-based srcset cannot fix that, because every
   *  width is a scaling of the same composition. */
  imageBaseMobile?: string;
  /** Serve AVIF ahead of the WebP. OPT-IN, and it must stay that way: a
   *  <source> whose file 404s does NOT fall through to the next candidate — the
   *  browser has already committed to that source and renders nothing. Only the
   *  Sales hero has .avif files beside its .webp, so only Sales sets this. Turn
   *  it on elsewhere strictly after generating the files for that imageBase. */
  heroAvif?: boolean;
  /** Also feather the photo's TOP + RIGHT edges into cream (base fades only
   *  cover left + bottom). The Design portrait's warm painterly wall needs it
   *  so its top/right padding dissolves instead of reading as a pasted block. */
  blendEdges?: boolean;
  /** Hide the per-date seat meter + count entirely (show dates only, no
   *  numbers). Used by the Design page, which surfaces real dates without a
   *  synthetic/real seat count. Defaults false → Cowork is unchanged. */
  hideMeter?: boolean;
  /** Print the session's time range and platform UNDER the date on each open
   *  card, keeping the card's existing shape (small day-of-week, big date) so an
   *  open card and a sold-out one still read as the same object. Pure data — the
   *  session's own start/end and the event's `platform` — so there is nothing
   *  here to go stale. Off by default: Cowork and Code are untouched. */
  showSessionMeta?: boolean;
  /** "stacked" (default): small weekday over a big "Sep 15". "inline": one
   *  line, "Tuesday, September 15", and the weekday span is dropped (the
   *  Last-call eyebrow still shows when the clock is in last call). */
  dateLine?: "stacked" | "inline";
  /** One live session, shown as an event rather than a picker: a calendar tile
   *  beside the date, time and place (Claude for Beginners, Esther 2026-10-26:
   *  "make it feel like it's one webinar"). Only applies when exactly one
   *  session is open and none are sold out; otherwise the strip renders. */
  singleEvent?: boolean;
  /** Optional clip that plays ONCE on load and then holds on its last frame,
   *  which stays as the hero. Desktop opens on its first frame rather than the
   *  photo, so there is no photo-then-cut (the AI Video page's "Dave turns
   *  to camera"). Holding beats handing back to the photo: the two never line
   *  up exactly at every width. Phones and desktop, never under reduced motion; if
   *  it fails or autoplay is refused, the photo simply stays. */
  introVideo?: { src: string; poster: string; srcSmall?: string };
}

export function CoworkHero({
  eyebrow,
  headline,
  headlinePlain,
  subtitle,
  priceLine,
  priceStrike,
  hairlineStops,
  onSelectSession,
  primaryCta,
  secondaryCta,
  imageBase = IMG_BASE,
  imageBaseMobile,
  heroAvif = false,
  blendEdges = false,
  singleEvent = false,
  hideMeter = false,
  showSessionMeta = false,
  dateLine = "stacked",
  introVideo,
}: CoworkHeroProps) {
  const introRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = introRef.current;
    const bg = v?.parentElement;
    if (!v || !bg || !introVideo) return;
    const done = () => { bg.dataset.intro = "done"; };
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { v.pause(); done(); return; }
    // The page opens on the clip's first frame (its poster). The browser
    // autoplays it as soon as it can, before hydration; this only takes over
    // if autoplay hasn't started (then it waits until it can play straight
    // through, so it never stalls on screen). Ended, it holds its last frame.
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      v.play().then(() => { bg.dataset.intro = "playing"; }).catch(done);
    };
    // autoPlay starts it before React does; pick up wherever it already is.
    if (v.ended) { bg.dataset.intro = "held"; return; }
    v.addEventListener("ended", () => { bg.dataset.intro = "held"; }, { once: true });
    v.addEventListener("error", done, { once: true });
    if (!v.paused) { started = true; bg.dataset.intro = "playing"; }
    else if (v.readyState >= 4) start();
    else v.addEventListener("canplaythrough", start, { once: true });
    // Too slow to buffer: hand the hero back to the photo.
    const giveUp = window.setTimeout(() => { if (!started) done(); }, 6000);
    return () => window.clearTimeout(giveUp);
  }, [introVideo]);

  // Index of the first open (upcoming) card — gets a subtle active highlight so
  // the soonest bookable date draws the eye.
  const firstOpenIdx = hairlineStops.findIndex((s) => s.kind === "open");

  return (
    <section className={`ch-hero${blendEdges ? " ch-hero--design" : ""}`} aria-label="Workshop hero">
      {/* Background: photo bleeding from the right + seamless cream gradient
          (left→right) + bottom cream fade. Background gradients are allowed —
          the "no gradients" rule is for buttons / section fills. */}
      <div className="ch-bg" aria-hidden="true" data-intro={introVideo ? "pending" : undefined}>
        {/* <picture> rather than a bare <img>: the mobile plate is a different
            COMPOSITION, not a different size, and srcset only chooses between
            resolutions of one image. The media query matches the CSS breakpoint
            the mobile hero rules use. */}
        <picture>
          {/* AVIF first — <source> order is preference order. Measured on the
              Sales hero: 20% smaller across the whole set, and 374KB -> 288KB on
              the file that IS the LCP element. The WebP sources below stay as the
              fallback for browsers without AVIF. */}
          {heroAvif && imageBaseMobile && (
            <source
              media="(max-width: 767px)"
              type="image/avif"
              srcSet={`${imageBaseMobile}-480.avif 480w, ${imageBaseMobile}-800.avif 800w, ${imageBaseMobile}-1200.avif 1200w`}
              sizes="100vw"
            />
          )}
          {imageBaseMobile && (
            <source
              media="(max-width: 767px)"
              srcSet={`${imageBaseMobile}-480.webp 480w, ${imageBaseMobile}-800.webp 800w, ${imageBaseMobile}-1200.webp 1200w`}
              sizes="100vw"
            />
          )}
          {heroAvif && (
            <source
              type="image/avif"
              srcSet={`${imageBase}-480.avif 480w, ${imageBase}-800.avif 800w, ${imageBase}-1200.avif 1200w, ${imageBase}.avif 1600w`}
              sizes="(max-width: 768px) 100vw, 68vw"
            />
          )}
          <img
            className="ch-photo"
            src={`${imageBase}.webp`}
            srcSet={`${imageBase}-480.webp 480w, ${imageBase}-800.webp 800w, ${imageBase}-1200.webp 1200w, ${imageBase}.webp 1600w`}
            sizes="(max-width: 768px) 100vw, 68vw"
            alt=""
            loading="eager"
            decoding="async"
            /* MERGE NOTE (2026-09-01): both sides of this element were right and
               keeping only one was a regression either way. The <picture> and its
               mobile <source> come from the Sales branch, which needs a separate
               mobile COMPOSITION rather than a smaller crop. Everything below
               comes from main's LCP pass and must survive inside it.

               This img is the hero's LCP element on every workshop page. `eager`
               only opts it out of lazy-loading; it still queues behind the
               preload scanner's other finds. fetchPriority promotes it.
               width/height carry the intrinsic ratio (1600x893) so the box is
               reserved even in the window before the stylesheet applies — CSS
               sizes it absolutely, so these never affect the rendered box. */
            fetchPriority="high"
            width={1600}
            height={893}
          />
        </picture>
        {introVideo && (
          <video
            ref={introRef}
            className="ch-intro"
            poster={introVideo.poster}
            muted
            playsInline
            autoPlay
            preload="auto"
            aria-hidden="true"
          >
            {/* Phones take the smaller encode when there is one. */}
            {introVideo.srcSmall && <source src={introVideo.srcSmall} type="video/mp4" media="(max-width: 767px)" />}
            <source src={introVideo.src} type="video/mp4" />
          </video>
        )}
        <div className="ch-fade-x" />
        <div className="ch-fade-y" />
        {blendEdges && <div className="ch-fade-top" />}
        {blendEdges && <div className="ch-fade-right" />}
      </div>

      <div className="ch-inner container-content">
        {/* Copy column — left aligned */}
        <div className="ch-copy">
          {/* An empty eyebrow renders nothing (Claude for Beginners runs without
              one; AI Video drops "New workshop series" while it has dates). */}
          {eyebrow && (
            <div className="ch-eyebrow">
              <span className="ch-eyebrow-dot" aria-hidden="true" />
              <span className="ch-eyebrow-label">{eyebrow}</span>
            </div>
          )}
          {headlinePlain ? (
            <>
              {/* The <h1> holds ONLY the clean sentence, once — screen readers,
                  raw text extraction and SEO all see exactly that. The stylized
                  headline is the aria-hidden sibling: same class, so the whole
                  `.ch-h1` cascade still applies, but its decorative insert can
                  duplicate and reorder words without any of that reaching the
                  heading's accessible name. */}
              <h1 className="sr-only">{headlinePlain}</h1>
              <div className="ch-h1" aria-hidden="true">{headline}</div>
            </>
          ) : (
            <h1 className="ch-h1">{headline}</h1>
          )}
          {/* Price — hero-price test only. Directly under the headline, above
              the subtitle: below it, the price sat at or under the fold on
              iPhone-size screens, so the test arm barely differed. */}
          {priceLine && (
            <p className="ch-sub ch-price" data-testid="hero-price">
              {priceStrike && <s className="ch-price-strike">{priceStrike}</s>}
              {priceLine}
            </p>
          )}
          {subtitle && <p className="ch-sub">{subtitle}</p>}
          <div className="ch-buttons">
            <a
              href={primaryCta.href}
              className="btn btn-cta btn-lg ch-btn"
              onClick={(e) => {
                // OPENS THE CHECKOUT (sheet on mobile, drawer on desktop); anchor-scroll runs only when nothing is bookable.
                if (requestCheckout()) e.preventDefault();
              }}
            >
              {primaryCta.label}
            </a>
            <a href={secondaryCta.href} className="btn btn-outline btn-lg ch-btn">
              {secondaryCta.label}
            </a>
          </div>
        </div>

        {/* Date strip — one card per session, shared-border grid. Sold-out
            (past) sessions show a terra SOLD OUT badge; open sessions show a
            day-of-week / last-call eyebrow + segmented seat meter + count. The
            sold-out cards stay visible to signal an in-demand recurring series. */}
        {(() => {
          const open = hairlineStops.filter((st) => st.kind === "open");
          if (!singleEvent || open.length !== 1 || open.length !== hairlineStops.length) return null;
          const st = open[0];
          const tz = st.timezone;
          const part = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat("en-US", { ...o, timeZone: tz }).format(st.startDatetime);
          const hours = st.endDatetime ? Math.round(((st.endDatetime.getTime() - st.startDatetime.getTime()) / 36e5) * 2) / 2 : null;
          return (
            <div className="ch-event" role="group" aria-label="Workshop date">
              <div className="ch-event-tile" aria-hidden="true">
                <span className="ch-event-month">{part({ month: "short" })}</span>
                <span className="ch-event-day">{part({ day: "numeric" })}</span>
                <span className="ch-event-dow">{part({ weekday: "short" })}</span>
              </div>
              <div className="ch-event-body">
                <span className="ch-event-kicker">Live workshop · One session</span>
                <span className="ch-event-date">{inlineDateFmt(st.startDatetime, tz)}</span>
                {st.endDatetime && (
                  <span className="ch-event-when">
                    {timeRangeFmt(st.startDatetime, st.endDatetime, tz)}
                    {st.where ? ` on ${st.where}` : ""}
                    {hours ? ` · ${hours} hours, hands-on` : ""}
                  </span>
                )}
              </div>
            </div>
          );
        })() ?? (
        <div className="ch-dates" role="group" aria-label="Workshop dates">
          {hairlineStops.map((stop, idx) => {
            const dow = dayOfWeekFmt(stop.startDatetime, stop.timezone);
            const dateStr = monthDayFmt(stop.startDatetime, stop.timezone);

            if (stop.kind === "soldout") {
              return (
                <div key={stop.key} className="ch-date-card ch-date-card--soldout">
                  {dateLine === "inline" ? (
                    <span className="ch-card-date ch-card-date--inline">{inlineDateFmt(stop.startDatetime, stop.timezone)}</span>
                  ) : (
                    <>
                      <span className="ch-card-dow">{dow}</span>
                      <span className="ch-card-date">{dateStr}</span>
                    </>
                  )}
                  {/* The same time-and-place line the open cards carry (Isaac,
                      2026-09-09: "the sold-out box should also have the same
                      time line as the other boxes"). Under the date, above the
                      badge, so a sold-out card and an open one are visibly the
                      same object with the seats gone. Still formatted from the
                      session's own start/end, so nothing here can go stale. */}
                  {showSessionMeta && stop.endDatetime && (
                    <span className="ch-card-when">
                      {timeRangeFmt(stop.startDatetime, stop.endDatetime, stop.timezone)}
                      {stop.where ? ` on ${stop.where}` : ""}
                    </span>
                  )}
                  <span className="ch-soldout-badge">Sold out</span>
                </div>
              );
            }

            // Desktop renders 6 segments; mobile collapses to 4 (CSS toggles
            // which meter is visible). Both derived from the same clock.
            const deskMeter = deriveMeter(stop.clock, stop.startingCount, 6);
            const mobMeter = deriveMeter(stop.clock, stop.startingCount, 4);
            const clickable = !!onSelectSession;
            const isActive = idx === firstOpenIdx;

            const cardBody = (
              <>
                {isLaborDaySession(stop.startDatetime, stop.timezone) && (
                  <span className="ch-card-callout" aria-hidden="true">LABOR DAY SPECIAL! 🇺🇸</span>
                )}
                {deskMeter.isLastCall && <span className="ch-card-lastcall">Last call</span>}
                {dateLine === "inline" ? (
                  <span className="ch-card-date ch-card-date--inline">{inlineDateFmt(stop.startDatetime, stop.timezone)}</span>
                ) : (
                  <>
                    {!deskMeter.isLastCall && <span className="ch-card-dow">{dow}</span>}
                    <span className="ch-card-date">{dateStr}</span>
                  </>
                )}
                {/* Time AND place on ONE line (Esther, 2026-09-08). They were
                    two spans, which stacked into two 11px lines under the date
                    and made the card look like it had a paragraph in it. One
                    string reads as a single fact: when and where. Still data
                    only — the range is formatted from the session and the
                    platform comes off the event row. */}
                {showSessionMeta && stop.endDatetime && (
                  <span className="ch-card-when">
                    {timeRangeFmt(stop.startDatetime, stop.endDatetime, stop.timezone)}
                    {stop.where ? ` on ${stop.where}` : ""}
                  </span>
                )}
                {!hideMeter && (
                  <span className="ch-card-meterwrap">
                    <span className="ch-meter-desktop">
                      <SeatMeter filled={deskMeter.filled} total={6} color={deskMeter.color} />
                    </span>
                    <span className="ch-meter-mobile">
                      <SeatMeter filled={mobMeter.filled} total={4} color={mobMeter.color} />
                    </span>
                    <span className="ch-card-seats">{deskMeter.text}</span>
                  </span>
                )}
              </>
            );

            const cls = `ch-date-card ch-date-card--open${isActive ? " ch-date-card--active" : ""}`;

            return clickable ? (
              <button
                key={stop.key}
                type="button"
                className={`${cls} ch-date-card--action`}
                onClick={() => onSelectSession!(stop.sessionId)}
                /* No aria-label: the card's own text (day, date, seat note) is
                   the accessible name. An aria-label that omitted the seat note
                   broke WCAG 2.5.3 Label in Name, so voice control could not
                   address the card by what it visibly reads. The enclosing
                   group carries the "Workshop dates" context. */
              >
                {cardBody}
              </button>
            ) : (
              <div key={stop.key} className={cls}>
                {cardBody}
              </div>
            );
          })}
        </div>
        )}
      </div>
    </section>
  );
}
